import React, { useCallback, useRef, useState } from "react";
import { mergeClassNames } from "../../design-tokens";
import { Button } from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import styles from "./FileUploader.module.css";

// ── Types ──

export interface ExistingFile {
  id: string;
  fileName: string;
  contentType?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

export interface FileUploaderLabels {
  title?: string;
  choose?: string;
  dragAndDrop?: string;
  dragHint?: string;
  newFilesSection?: string;
  existingFilesSection?: string;
  noFiles?: string;
  download?: string;
  delete?: string;
  remove?: string;
}

export interface FileUploaderProps {
  /** Arquivos já existentes (vindos da API) */
  existingFiles?: ExistingFile[];
  /** Novos arquivos selecionados pelo usuário */
  files: File[];
  /** Callback quando a lista de novos arquivos muda */
  onFilesChange: (files: File[]) => void;
  /** Callback para download de arquivo existente */
  onDownload?: (file: ExistingFile) => void;
  /** Callback para remoção de arquivo existente */
  onDeleteExisting?: (file: ExistingFile) => void;
  /** Tipos de arquivo aceitos (default: qualquer) */
  accept?: string;
  /** Tamanho máximo por arquivo em bytes (default: 10MB) */
  maxFileSize?: number;
  /** Permite múltiplos arquivos */
  multiple?: boolean;
  /** Desabilita todo o componente */
  disabled?: boolean;
  /** Labels customizáveis */
  labels?: FileUploaderLabels;
  /** Classes CSS adicionais */
  className?: string;
  /** Esconde o ícone do cabeçalho */
  hideIcon?: boolean;
  /** Esconde o botão "Escolher arquivos" no cabeçalho */
  hideChooseButton?: boolean;
}

// ── Helpers ──

const DEFAULT_LABELS: Required<FileUploaderLabels> = {
  title: "Anexos",
  choose: "Escolher arquivos",
  dragAndDrop: "Arraste arquivos aqui ou clique para selecionar",
  dragHint: "Solte os arquivos aqui",
  newFilesSection: "Novos arquivos",
  existingFilesSection: "Arquivos anexados",
  noFiles: "Nenhum arquivo anexado.",
  download: "Baixar",
  delete: "Excluir",
  remove: "Remover",
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

type FileIconInfo = { icon: string; className: string };

const resolveFileIcon = (contentTypeOrName: string): FileIconInfo => {
  const s = (contentTypeOrName || "").toLowerCase();

  if (s.includes("pdf"))
    return { icon: "picture_as_pdf", className: styles.fileIconPdf };
  if (s.includes("image") || /\.(png|jpe?g|gif|svg|webp|bmp)$/i.test(s))
    return { icon: "image", className: styles.fileIconImage };
  if (s.includes("sheet") || s.includes("excel") || /\.(xlsx?|csv|ods)$/i.test(s))
    return { icon: "table_chart", className: styles.fileIconSpreadsheet };
  if (s.includes("word") || s.includes("document") || /\.(docx?|odt|rtf)$/i.test(s))
    return { icon: "description", className: styles.fileIconDocument };
  if (s.includes("presentation") || s.includes("powerpoint") || /\.(pptx?|odp)$/i.test(s))
    return { icon: "slideshow", className: styles.fileIconDocument };
  if (s.includes("zip") || s.includes("archive") || /\.(zip|rar|7z|tar|gz)$/i.test(s))
    return { icon: "folder_zip", className: styles.fileIconArchive };

  return { icon: "attach_file", className: styles.fileIconGeneric };
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ── Component ──

export const FileUploader: React.FC<FileUploaderProps> = ({
  existingFiles = [],
  files,
  onFilesChange,
  onDownload,
  onDeleteExisting,
  accept,
  maxFileSize = 10 * 1024 * 1024,
  multiple = true,
  disabled = false,
  labels: userLabels,
  className,
  hideIcon = false,
  hideChooseButton = false,
}) => {
  const labels = { ...DEFAULT_LABELS, ...userLabels };
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── File selection ──

  const addFiles = useCallback(
    (incoming: FileList | File[]): void => {
      const arr = Array.from(incoming).filter((f) => f.size <= maxFileSize);
      if (arr.length === 0) return;
      onFilesChange(multiple ? [...files, ...arr] : [arr[0]]);
    },
    [files, maxFileSize, multiple, onFilesChange],
  );

  const removeNewFile = useCallback(
    (index: number): void => {
      onFilesChange(files.filter((_, i) => i !== index));
    },
    [files, onFilesChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (e.target.files) addFiles(e.target.files);
      // Reset so the same file can be selected again
      e.target.value = "";
    },
    [addFiles],
  );

  // ── Drag & drop ──

  const handleDragOver = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((): void => setIsDragging(false), []);

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      setIsDragging(false);
      if (!disabled && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    },
    [addFiles, disabled],
  );

  // ── Render helpers ──

  const renderFileIcon = (iconInfo: FileIconInfo): React.ReactNode => (
    <span
      className={mergeClassNames("material-symbols-outlined", styles.fileIcon, iconInfo.className)}
      aria-hidden="true"
    >
      {iconInfo.icon}
    </span>
  );

  const hasNewFiles = files.length > 0;
  const hasExistingFiles = existingFiles.length > 0;
  const hasAnyFiles = hasNewFiles || hasExistingFiles;

  return (
    <div className={mergeClassNames(styles.root, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>
          {!hideIcon && (
            <span className="material-symbols-outlined" aria-hidden="true">attach_file</span>
          )}
          {labels.title}
        </h3>
        {!hideChooseButton && (
          <Button
            label={labels.choose}
            icon="upload_file"
            variant="outlined"
            severity="secondary"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        className={styles.hiddenInput}
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        disabled={disabled}
      />

      {/* Drop zone */}
      <div
        className={mergeClassNames(
          styles.dropZone,
          isDragging && styles.dropZoneDragging,
          disabled && styles.dropZoneDisabled,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={labels.dragAndDrop}
      >
        <span className={mergeClassNames("material-symbols-outlined", styles.dropZoneIcon)} aria-hidden="true">
          cloud_upload
        </span>
        <span className={styles.dropZoneText}>
          {isDragging ? labels.dragHint : labels.dragAndDrop}
        </span>
        {maxFileSize && (
          <span className={styles.dropZoneHint}>
            Máx. {formatFileSize(maxFileSize)} por arquivo
          </span>
        )}
      </div>

      {/* New files */}
      {hasNewFiles && (
        <div className={styles.fileList}>
          <p className={styles.sectionLabel}>{labels.newFilesSection}</p>
          {files.map((file, index) => {
            const iconInfo = resolveFileIcon(file.name);
            return (
              <div key={`new-${index}`} className={styles.fileItem}>
                {renderFileIcon(iconInfo)}
                <div className={styles.fileInfo}>
                  <div className={styles.fileName} title={file.name}>{file.name}</div>
                  <div className={styles.fileMeta}>{formatFileSize(file.size)}</div>
                </div>
                <div className={styles.fileActions}>
                  <IconButton
                    icon="close"
                    variant="text"
                    severity="danger"
                    aria-label={labels.remove}
                    onClick={() => removeNewFile(index)}
                    disabled={disabled}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Existing files */}
      {hasExistingFiles && (
        <div className={styles.fileList}>
          <p className={styles.sectionLabel}>{labels.existingFilesSection}</p>
          {existingFiles.map((file) => {
            const iconInfo = resolveFileIcon(file.contentType || file.fileName);
            return (
              <div key={file.id} className={styles.fileItem}>
                {renderFileIcon(iconInfo)}
                <div className={styles.fileInfo}>
                  <div className={styles.fileName} title={file.fileName}>{file.fileName}</div>
                  {(file.uploadedBy || file.uploadedAt) && (
                    <div className={styles.fileMeta}>
                      {file.uploadedBy && <span>{file.uploadedBy}</span>}
                      {file.uploadedBy && file.uploadedAt && <span> · </span>}
                      {file.uploadedAt && <span>{formatDate(file.uploadedAt)}</span>}
                    </div>
                  )}
                </div>
                <div className={styles.fileActions}>
                  {onDownload && (
                    <IconButton
                      icon="download"
                      variant="text"
                      severity="primary"
                      aria-label={labels.download}
                      onClick={() => onDownload(file)}
                    />
                  )}
                  {onDeleteExisting && (
                    <IconButton
                      icon="delete"
                      variant="text"
                      severity="danger"
                      aria-label={labels.delete}
                      onClick={() => onDeleteExisting(file)}
                      disabled={disabled}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!hasAnyFiles && (
        <div className={styles.emptyState}>{labels.noFiles}</div>
      )}
    </div>
  );
};

export default FileUploader;
